import { inject, Injectable, Signal, signal } from '@angular/core';
import { initialize, LDClient } from 'launchdarkly-js-client-sdk';
import { environment } from '@brighthub/environment';
import { AuthService } from '@brightside/core/auth';
import { Observable, from, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { StructuredLogger } from '@brighthub/logging';

/** Seconds to wait for LD client initialization before falling back to defaults. */
const LD_INIT_TIMEOUT_SEC = 5;

export type LDFlagValue = string | number | boolean | undefined;

/** Map of flag key to value (e.g. from getAllFlags$). */
export type LDFlagSet = Record<string, LDFlagValue>;

/** User identity from auth (e.g. Okta userinfo). Use this to identify in LaunchDarkly — do not send access tokens. */
export interface FeatureFlagUserContext {
  /** Stable unique key (e.g. Okta `sub`). Required. */
  key: string;
  /** Display name for LD dashboard. Optional. */
  name?: string;
  /** Email for targeting. Optional. */
  email?: string;
  /** Custom attributes for targeting (e.g. groups, role). Optional. */
  custom?: Record<string, string | string[] | number | boolean>;
}

/**
 * Thin Angular wrapper around the LaunchDarkly JavaScript client.
 * Injected app-wide when provideFeatureFlags() is used in app.config.
 * Does not initialize until we have authenticated user info; no anonymous context.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly logs = inject(StructuredLogger);
  private readonly authService = inject(AuthService);
  private client: LDClient | null = null;
  private initPromise: Promise<void> | null = null;
  private authListenerSetup = false;
  /** Resolved when client is ready or we know we won't have one (so getValue$/getValueChanges$ can wait). */
  private clientReadyPromise: Promise<LDClient | null> | null = null;
  private clientReadyResolve: ((client: LDClient | null) => void) | null = null;

  /**
   * Set up auth listener only. Called by provideAppInitializer.
   * When the user is authenticated, we initialize the LD client with their context (no anonymous init).
   */
  startAuthListener(): void {
    if (this.authListenerSetup) return;
    this.authListenerSetup = true;
    this.ensureClientReadyDeferred();

    const tryInitWithUser = async (): Promise<void> => {
      try {
        const user = await this.authService.getUser();
        const groups = this.authService.getUserGroups();
        await this.init(this.userClaimsToContext(user, groups));
      } catch {
        // No user info; do not initialize (no anonymous)
      }
      this.resolveClientReady(this.client);
    };

    this.authService._oktaAuth.authStateManager.subscribe(async () => {
      const authenticated = await this.authService.isAuthenticated();
      if (authenticated) {
        await tryInitWithUser();
      } else {
        this.close();
      }
    });

    // Already authenticated on load
    this.authService.isAuthenticated().then((authenticated) => {
      if (authenticated) tryInitWithUser();
    });
  }

  private ensureClientReadyDeferred(): void {
    if (this.clientReadyPromise) return;
    this.clientReadyPromise = new Promise<LDClient | null>((resolve) => {
      this.clientReadyResolve = resolve;
    });
  }

  private resolveClientReady(client: LDClient | null): void {
    if (this.clientReadyResolve) {
      this.clientReadyResolve(client);
      this.clientReadyResolve = null;
    }
    this.clientReadyPromise = null;
  }

  /**
   * Wait until the LD client is ready (after auth + init) or we know we won't get one. Used internally by
   * getValue$ / getValueChanges$; expose if you need to wait before other LD calls.
   *
   * @remarks When {@link startAuthListener} has not run (e.g. unit tests or missing provideFeatureFlags()),
   * resolves immediately with `null` so callers can fall back to defaults instead of hanging.
   */
  waitForClient(): Promise<LDClient | null> {
    if (this.client) return Promise.resolve(this.client);
    if (!this.authListenerSetup) return Promise.resolve(null);
    return this.clientReadyPromise ?? Promise.resolve(null);
  }

  /**
   * Initialize the LaunchDarkly client with the given user context only.
   * Called after we have user info; no anonymous or default-flag fetch.
   */
  async init(context: FeatureFlagUserContext): Promise<void> {
    if (this.client) return;
    if (this.initPromise) return this.initPromise;

    const clientSideId = environment.launchdarkly?.['clientSideId'];
    if (!clientSideId) {
      this.logs.emit('feature_flags.configuration_missing', {
        feature: 'feature_flags',
        stage: 'initialization',
        reason_code: 'missing_configuration',
      });
      console.warn(
        '[FeatureFlags] No launchdarkly.clientSideId in environment; feature flags disabled.',
      );
      return;
    }

    const ldContext = this.toLDContext(context);

    this.initPromise = (async () => {
      try {
        this.client = initialize(clientSideId, ldContext);
        await this.client.waitForInitialization(LD_INIT_TIMEOUT_SEC);
      } catch (err) {
        this.logs.emit('feature_flags.initialization_failed', {
          feature: 'feature_flags',
          stage: 'initialization',
          reason_code: 'dependency_unavailable',
        });
        console.error('[FeatureFlags] Failed to initialize LaunchDarkly:', err);
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  private toLDContext(context: FeatureFlagUserContext): Record<string, unknown> {
    const ldContext: Record<string, unknown> = {
      kind: 'user',
      key: context.key,
      anonymous: context.key === 'anonymous',
    };
    if (context['name']) ldContext['name'] = context['name'];
    if (context['email']) ldContext['email'] = context['email'];
    if (context['custom']) {
      Object.entries(context['custom']).forEach(([k, v]) => { ldContext[k] = v; });
    }
    return ldContext;
  }

  /**
   * Build LaunchDarkly context from Okta UserClaims and groups.
   * Uses userinfo (identity) only — never the access token.
   */
  private userClaimsToContext(
    user: {
      sub?: string;
      name?: string;
      given_name?: string;
      family_name?: string;
      email?: string;
    },
    groups: string[],
  ): FeatureFlagUserContext {
    const key = user.sub ?? 'anonymous';
    const name = user.name ?? ([user.given_name, user.family_name].filter(Boolean).join(' ').trim() || undefined);
    const custom: Record<string, string | string[] | boolean> = {};
    if (groups.length) custom['groups'] = groups;
    return { key, name, email: user.email, custom: Object.keys(custom).length ? custom : undefined };
  }

  /**
   * Identify a new context (e.g. after re-login). Call with userinfo-derived identity — do not pass tokens.
   * Use only when the client was already initialized and the user context changed.
   */
  identifyFromUser(context: FeatureFlagUserContext): void {
    if (!this.client) return;
    this.client.identify(this.toLDContext(context)).catch((err) => {
      this.logs.emit('feature_flags.identification_failed', {
        feature: 'feature_flags',
        stage: 'identification',
        reason_code: 'dependency_unavailable',
      });
      console.warn('[FeatureFlags] identify failed:', err);
    });
  }

  /**
   * Return a signal that holds the flag value. Call once in the component class (e.g. as a readonly
   * field), then read the signal in the template (e.g. `faVariant()`). Do not call this from the
   * template — it would create a new signal and subscription every change detection. Uses getValue$()
   * under the hood (one-off value after client is ready).
   */
  flagValue<T extends LDFlagValue = LDFlagValue>(flagKey: string, defaultValue: T): Signal<T> {
    const sig = signal<T>(defaultValue);
    this.getValue$(flagKey, defaultValue).subscribe((value) => sig.set(value as T));
    return sig;
  }

  /**
   * Observe flag value once. Waits for client to be ready, then emits once and completes.
   * If we never get a client (e.g. not authenticated), emits defaultValue and completes.
   */
  getValue$(flagKey: string, defaultValue: LDFlagValue): Observable<LDFlagValue> {
    return from(this.waitForClient()).pipe(
      mergeMap((client) => {
        if (!client) return of(defaultValue);
        return from(client.waitForInitialization(LD_INIT_TIMEOUT_SEC)).pipe(
          map(() => client.variation(flagKey, defaultValue)),
          catchError(() => of(defaultValue))
        );
      })
    );
  }

  /**
   * Get all flag values in one shot. Waits for client to be ready, then emits once with a map of
   * flag key to value (from the LD client's allFlags()) and completes. If we never get a client,
   * emits an empty object.
   */
  getAllFlags$(): Observable<LDFlagSet> {
    return from(this.waitForClient()).pipe(
      mergeMap((client) => {
        if (!client) return of({});
        return from(client.waitForInitialization(LD_INIT_TIMEOUT_SEC)).pipe(
          map(() => (client as { allFlags(): LDFlagSet }).allFlags()),
          catchError(() => of({}))
        );
      })
    );
  }

  /**
   * Observe flag value and all future changes. Waits for client to be ready, then emits initial value
   * and every change (LD streaming). If we never get a client, emits defaultValue and completes.
   */
  getValueChanges$(flagKey: string, defaultValue: LDFlagValue): Observable<LDFlagValue> {
    return from(this.waitForClient()).pipe(
      mergeMap((client) => {
        if (!client) return of(defaultValue);
        return from(client.waitForInitialization(LD_INIT_TIMEOUT_SEC)).pipe(
          mergeMap(() => {
            return new Observable<LDFlagValue>((subscriber) => {
              subscriber.next(client.variation(flagKey, defaultValue));
              const changeKey = `change:${flagKey}`;
              const handler = (current: LDFlagValue): void => subscriber.next(current);
              client.on(changeKey, handler);
              return () => client.off(changeKey, handler);
            });
          }),
          catchError(() => of(defaultValue))
        );
      })
    );
  }

  /**
   * Close the client and flush events. Call when the app is tearing down or user logs out.
   */
  close(): void {
    if (this.client) {
      this.client.close();
      this.client = null;
      this.initPromise = null;
    }
    this.resolveClientReady(null);
    this.ensureClientReadyDeferred(); // next waitForClient() will wait for next login
  }
}
